import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  ButtonBase,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  InputAdornment,
  Skeleton,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded';
import SavingsRounded from '@mui/icons-material/SavingsRounded';
import { useAsync } from '@/hooks/useAsync';
import { useDebounced } from '@/hooks/useDebounced';
import { getQuote } from './api';
import { formatMoney } from './labels';
import { normalizeChoice } from './choice';
import type { Catalog, CycleKey, Plan, PlanChoice, Quote } from './types';

/** Slider stops: fine steps for small schools, coarse ones for big universities. */
const MARKS = [30, 50, 100, 150, 200, 300, 400, 500, 750, 1000, 1500, 2000, 3000, 5000, 10000];

function toSlider(students: number) {
  const index = MARKS.findIndex((m) => m >= students);
  return index === -1 ? MARKS.length - 1 : index;
}

/** Price per student per month after the cycle and volume discounts (cards only). */
function effectiveStudentCents(catalog: Catalog, plan: Plan, cycle: CycleKey, students: number) {
  const seats = Math.max(students, plan.minStudents);
  const volume =
    [...catalog.volumeTiers].reverse().find((t) => seats >= t.minStudents)?.discount ?? 0;
  const cycleDiscount = plan.cycles.includes(cycle)
    ? (catalog.cycles.find((c) => c.key === cycle)?.discount ?? 0)
    : 0;
  return Math.round(plan.studentCents * (1 - volume / 100) * (1 - cycleDiscount / 100));
}

interface PlanPickerProps {
  catalog: Catalog;
  value: PlanChoice;
  onChange: (next: PlanChoice) => void;
  /** Narrow column (dialogs, the sign-up form): no bullet lists, two plans per row. */
  dense?: boolean;
}

/** Billing cycle, number of students, plan and add-ons. */
export function PlanPicker({ catalog, value, onChange, dense }: PlanPickerProps) {
  const [studentsText, setStudentsText] = useState(String(value.students));
  const [lastStudents, setLastStudents] = useState(value.students);
  if (value.students !== lastStudents) {
    setLastStudents(value.students);
    setStudentsText(String(value.students));
  }
  const set = (patch: Partial<PlanChoice>) =>
    onChange(normalizeChoice(catalog, { ...value, ...patch }));
  const recurringCycles = catalog.cycles.filter((c) => c.key !== 'once');
  const plan = catalog.plans.find((p) => p.key === value.plan);
  const addons = catalog.addons.filter((a) => a.plans.includes(value.plan));

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: dense ? '1fr' : 'auto minmax(0, 1fr)' },
          gap: 3,
          alignItems: 'center',
        }}
      >
        <Stack spacing={1}>
          <Typography variant="subtitle2" component="h3">
            Como prefere pagar
          </Typography>
          <ToggleButtonGroup
            exclusive
            color="primary"
            size="small"
            value={
              plan?.cycles.includes(value.cycle) && value.cycle !== 'once' ? value.cycle : null
            }
            onChange={(_, cycle: CycleKey | null) => {
              if (!cycle) return;
              // Passe ENADE has a single payment: a recurring cycle means a recurring plan.
              set(value.plan === 'passe_enade' ? { cycle, plan: 'pro' } : { cycle });
            }}
            aria-label="Ciclo de cobrança"
          >
            {recurringCycles.map((c) => (
              <ToggleButton key={c.key} value={c.key} sx={{ px: 2, gap: 1 }}>
                {c.name}
                {c.discount > 0 && (
                  <Chip
                    size="small"
                    variant="soft"
                    color="lime"
                    label={`−${c.discount}%`}
                    sx={{ height: 18, fontSize: '0.6875rem' }}
                  />
                )}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <Stack spacing={1}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" component="h3" id="students-label">
              Quantos estudantes vão usar
            </Typography>
            <TextField
              size="small"
              type="number"
              value={studentsText}
              onChange={(e) => {
                setStudentsText(e.target.value);
                const n = Number(e.target.value);
                if (Number.isInteger(n) && n >= 1 && n <= 100_000) set({ students: n });
              }}
              slotProps={{
                htmlInput: { min: 1, max: 100000, 'aria-labelledby': 'students-label' },
                input: { endAdornment: <InputAdornment position="end">estudantes</InputAdornment> },
              }}
              sx={{ width: 190 }}
            />
          </Stack>
          <Slider
            value={toSlider(value.students)}
            min={0}
            max={MARKS.length - 1}
            step={1}
            onChange={(_, index) => set({ students: MARKS[index as number] })}
            valueLabelDisplay="auto"
            valueLabelFormat={(index) => MARKS[index].toLocaleString('pt-BR')}
            aria-labelledby="students-label"
            getAriaValueText={(index) => `${MARKS[index]} estudantes`}
          />
          <Typography variant="caption" color="text.secondary">
            Desconto por volume:{' '}
            {catalog.volumeTiers
              .map((t) => `${t.discount}% a partir de ${t.minStudents.toLocaleString('pt-BR')}`)
              .join(' · ')}
          </Typography>
        </Stack>
      </Box>

      <Box
        role="radiogroup"
        aria-label="Plano"
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            lg: dense ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {catalog.plans.map((p) => {
          const selected = p.key === value.plan;
          const once = p.cycles.includes('once');
          const unit = once
            ? p.studentCents
            : effectiveStudentCents(catalog, p, value.cycle, value.students);
          return (
            <ButtonBase
              key={p.key}
              role="radio"
              aria-checked={selected}
              onClick={() =>
                set({
                  plan: p.key,
                  cycle: once ? 'once' : value.cycle === 'once' ? 'annual' : value.cycle,
                })
              }
              sx={{ textAlign: 'left', borderRadius: 5, display: 'block', height: '100%' }}
            >
              <Card
                sx={(t) => ({
                  height: '100%',
                  p: 2.5,
                  borderRadius: 5,
                  borderWidth: selected ? 2 : 1,
                  borderColor: selected ? t.vars.palette.primary.main : undefined,
                  bgcolor: selected
                    ? `color-mix(in srgb, ${t.vars.palette.primary.container} 45%, ${t.vars.palette.background.paper})`
                    : undefined,
                  transition: 'all 200ms',
                  '&:hover': { borderColor: t.vars.palette.primary.light },
                })}
              >
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                  {selected ? (
                    <CheckCircleRounded color="primary" fontSize="small" />
                  ) : (
                    <RadioButtonUncheckedRounded color="disabled" fontSize="small" />
                  )}
                  <Typography variant="h6" component="span" sx={{ flex: 1 }}>
                    {p.name}
                  </Typography>
                  {p.badge && (
                    <Chip
                      size="small"
                      variant="soft"
                      color={once ? 'warning' : 'primary'}
                      label={p.badge}
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                  {p.tagline}
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'baseline', mt: 1.5 }}>
                  <Typography
                    component="span"
                    sx={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em' }}
                  >
                    {formatMoney(unit)}
                  </Typography>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {once ? `por estudante · ${p.durationDays} dias` : 'por estudante/mês'}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div">
                  {!once && unit < p.studentCents && (
                    <>
                      <s>{formatMoney(p.studentCents)}</s> ·{' '}
                    </>
                  )}
                  mínimo de {p.minStudents} estudantes
                </Typography>
                {!dense && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack spacing={0.75}>
                      {p.highlights.map((h) => (
                        <Stack
                          key={h}
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'flex-start' }}
                        >
                          <CheckRounded sx={{ fontSize: 16, mt: 0.25, color: 'success.main' }} />
                          <Typography variant="body2">{h}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </>
                )}
              </Card>
            </ButtonBase>
          );
        })}
      </Box>

      {addons.length > 0 && (
        <Box>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
            Complementos do plano {plan?.name}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: dense ? '1fr' : '1fr 1fr' },
              gap: 1,
            }}
          >
            {addons.map((a) => {
              const checked = value.addons.includes(a.key);
              const price = a.studentCents
                ? `${formatMoney(a.studentCents)} por estudante/mês`
                : a.monthlyCents
                  ? `${formatMoney(a.monthlyCents)}/mês`
                  : `${formatMoney(a.onceCents)}, uma vez`;
              return (
                <Card key={a.key} sx={{ px: 1.5, py: 1, borderRadius: 4 }}>
                  <FormControlLabel
                    sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={() =>
                          set({
                            addons: checked
                              ? value.addons.filter((k) => k !== a.key)
                              : [...value.addons, a.key],
                          })
                        }
                        sx={{ mt: -0.5 }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {a.name}{' '}
                          <Typography component="span" variant="body2" color="primary">
                            + {price}
                          </Typography>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {a.description}
                        </Typography>
                      </Box>
                    }
                  />
                </Card>
              );
            })}
          </Box>
        </Box>
      )}
    </Stack>
  );
}

/** The price of the choice, line by line (from the API, so it matches the invoice). */
export function QuoteSummary({
  choice,
  trialDays,
  footer,
  onQuote,
}: {
  choice: PlanChoice;
  /** Days free before the first charge (0: none). */
  trialDays?: number;
  footer?: React.ReactNode;
  onQuote?: (quote: Quote | undefined) => void;
}) {
  const debounced = useDebounced(choice, 250);
  const quote = useAsync((signal) => getQuote(debounced, signal), [JSON.stringify(debounced)]);
  const q = quote.data;
  useEffect(() => {
    onQuote?.(q);
  }, [q, onQuote]);
  const per =
    q?.cycle === 'once'
      ? 'pagamento único'
      : q?.months === 1
        ? 'por mês'
        : q?.months === 6
          ? 'por semestre'
          : 'por ano';

  return (
    <Card sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 5 }}>
      <Typography variant="overline" color="text.secondary">
        Resumo
      </Typography>
      {quote.error ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          Não foi possível calcular o preço.
        </Alert>
      ) : !q ? (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={28} />
          ))}
        </Stack>
      ) : (
        <Box sx={{ opacity: quote.loading ? 0.6 : 1, transition: 'opacity 150ms' }}>
          <Typography variant="h6" component="p">
            {q.planName}{' '}
            <Typography component="span" color="text.secondary">
              · {q.cycleName.toLowerCase()} · {q.seats.toLocaleString('pt-BR')} estudantes
            </Typography>
          </Typography>
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {q.lines
              .filter((l) => l.kind !== 'setup')
              .map((l) => (
                <Stack
                  key={l.label}
                  direction="row"
                  spacing={2}
                  sx={{ justifyContent: 'space-between' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2">{l.label}</Typography>
                    {l.detail && (
                      <Typography variant="caption" color="text.secondary">
                        {l.detail}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                      color: l.kind === 'discount' ? 'success.main' : undefined,
                    }}
                  >
                    {formatMoney(l.amountCents)}
                  </Typography>
                </Stack>
              ))}
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Total {per}
            </Typography>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em' }}>
              {formatMoney(q.recurringCents)}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
            {q.cycle === 'once'
              ? `${q.durationDays} dias de acesso`
              : `equivale a ${formatMoney(q.monthlyCents)}/mês`}{' '}
            · {formatMoney(q.perStudentMonthCents)} por estudante/mês
          </Typography>
          {q.setupCents > 0 && (
            <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
              + {formatMoney(q.setupCents)} de{' '}
              {q.lines
                .filter((l) => l.kind === 'setup')
                .map((l) => l.label.toLowerCase())
                .join(', ')}
              , cobrado uma vez com a primeira fatura.
            </Alert>
          )}
          {q.savingsCents > 0 && (
            <Chip
              icon={<SavingsRounded />}
              variant="soft"
              color="lime"
              label={`Você economiza ${formatMoney(q.savingsCents)} ${per}`}
              sx={{ mt: 2 }}
            />
          )}
          {Boolean(trialDays) && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              <b>{trialDays} dias grátis:</b> a primeira cobrança vence só no fim do teste. Pague
              por PIX, boleto ou cartão.
            </Typography>
          )}
          {footer}
        </Box>
      )}
    </Card>
  );
}
